import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiPlay, HiLocationMarker, HiClock, HiMenu, HiX, HiArrowRight } from 'react-icons/hi';
import { IoRadioButtonOn } from 'react-icons/io5';
import './RushStyle.css';

// RUSH — Aggressive, diagonal cuts, high-energy. Think Red Bull meets Nike.
export default function RushStyle({ config }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games`);
      const data = await res.json();
      setGames(data.data.games || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const liveGames = games.filter(g => g.status === 'live');
  const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'ready');
  const recordedGames = games.filter(g => g.status === 'recorded' && g.recordingUrl);

  if (loading) return <div className="rush-loading"><span>RUSH</span></div>;

  return (
    <div className="rush">

      {/* Live Ticker */}
      {liveGames.length > 0 && (
        <div className="rush-ticker">
          <div className="rush-ticker-inner">
            {[...liveGames, ...liveGames].map((game, i) => (
              <span key={i} className="rush-ticker-item">
                <span className="rush-ticker-dot" />
                LIVE: {game.homeTeam} vs {game.awayTeam} — {game.venue}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`rush-header ${liveGames.length === 0 ? 'no-ticker' : ''}`}>
        <div className="rush-header-inner">
          <div className="rush-logo">
            <span className="rush-logo-slash">/</span>
            <span className="rush-logo-text">RUSH</span>
          </div>
          <nav className={`rush-nav ${menuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/schedule" onClick={() => setMenuOpen(false)}>Schedule</Link>
            <Link to="/replays" onClick={() => setMenuOpen(false)}>Replays</Link>
            <Link to="/login" className="rush-nav-cta" onClick={() => setMenuOpen(false)}>Sign In</Link>
          </nav>
          <button className="rush-burger" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </header>

      {/* Hero — diagonal split */}
      <section className="rush-hero">
        <div className="rush-hero-left">
          <div className="rush-hero-eyebrow">
            {liveGames.length > 0
              ? <><IoRadioButtonOn className="rush-live-dot" /> {liveGames.length} LIVE</>
              : 'YOUTH SPORTS STREAMING'
            }
          </div>
          <h1 className="rush-hero-headline">
            DON'T<br />MISS A<br /><span>MOMENT.</span>
          </h1>
          <p className="rush-hero-sub">Live games, instant replays, all in one place.</p>
          <Link to="/" className="rush-hero-btn">
            Watch Now <HiArrowRight />
          </Link>
        </div>
        <div className="rush-hero-right">
          {liveGames[0]?.thumbnailUrl
            ? <img src={liveGames[0].thumbnailUrl} alt="Live game" className="rush-hero-img" />
            : <div className="rush-hero-img-placeholder" />
          }
          {liveGames[0] && (
            <Link to={`/watch/${liveGames[0].gameId}`} className="rush-hero-game-card">
              <div className="rush-hero-game-live"><IoRadioButtonOn /> LIVE</div>
              <div className="rush-hero-game-teams">
                {liveGames[0].homeTeam} <span>vs</span> {liveGames[0].awayTeam}
              </div>
              <div className="rush-hero-game-venue"><HiLocationMarker /> {liveGames[0].venue}</div>
            </Link>
          )}
        </div>
      </section>

      <div className="rush-body">

        {/* Live games */}
        {liveGames.length > 0 && (
          <section className="rush-section">
            <div className="rush-section-header">
              <h2><IoRadioButtonOn className="rush-live-dot" /> LIVE NOW</h2>
            </div>
            <div className="rush-live-grid">
              {liveGames.map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="rush-live-card">
                  <div className="rush-live-card-bg" />
                  <div className="rush-live-badge">LIVE</div>
                  <div className="rush-live-sport">{game.sport?.toUpperCase()}</div>
                  <div className="rush-live-teams">
                    <span>{game.homeTeam}</span>
                    <span className="rush-live-vs">VS</span>
                    <span>{game.awayTeam}</span>
                  </div>
                  <div className="rush-live-venue"><HiLocationMarker /> {game.venue}</div>
                  <div className="rush-live-cta"><HiPlay /> WATCH <HiArrowRight /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming */}
        {upcomingGames.length > 0 && (
          <section className="rush-section">
            <div className="rush-section-header">
              <h2>UPCOMING</h2>
            </div>
            <div className="rush-upcoming-list">
              {upcomingGames.slice(0, 6).map((game, i) => (
                <div key={game.gameId} className="rush-upcoming-row">
                  <div className="rush-upcoming-num">0{i + 1}</div>
                  <div className="rush-upcoming-sport">{game.sport?.toUpperCase()}</div>
                  <div className="rush-upcoming-teams">
                    {game.homeTeam} <span>vs</span> {game.awayTeam}
                  </div>
                  <div className="rush-upcoming-meta">
                    <span><HiLocationMarker /> {game.venue}</span>
                    <span><HiClock /> {new Date(game.scheduledStart).toLocaleString('en-US', { 
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                    })}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Replays */}
        {recordedGames.length > 0 && (
          <section className="rush-section">
            <div className="rush-section-header">
              <h2>REPLAYS</h2>
            </div>
            <div className="rush-replays-grid">
              {recordedGames.map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="rush-replay-card">
                  <div className="rush-replay-thumb">
                    {game.thumbnailUrl
                      ? <img src={game.thumbnailUrl} alt="Match" />
                      : <div className="rush-replay-placeholder" />
                    }
                    <div className="rush-replay-overlay">
                      <HiPlay className="rush-replay-play" />
                    </div>
                  </div>
                  <div className="rush-replay-info">
                    <div className="rush-replay-sport">{game.sport?.toUpperCase()}</div>
                    <div className="rush-replay-teams">{game.homeTeam} vs {game.awayTeam}</div>
                    <div className="rush-replay-venue"><HiLocationMarker /> {game.venue}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
